import JSZip from 'jszip';
import { annotationText } from './feedbackAnnotations.js';

const DEVELOPER_CHECKLIST = `# Developer Checklist

- [ ] Open feedback-summary.md.
- [ ] Review each screenshot and annotated SVG.
- [ ] Confirm each route still exists.
- [ ] Identify impacted React component(s).
- [ ] Implement requested UI/content changes.
- [ ] Add or update component tests.
- [ ] Add or update Playwright proof if behavior is user-visible.
- [ ] Run format, lint, unit/component tests, build, and e2e gates.
- [ ] Confirm changes against annotated screenshots.
`;

function slugify(value, fallback = 'feedback') {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || fallback;
}

function timestampForFile(date = new Date()) {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, '')
    .replace('T', '-');
}

function screenBaseName(screen, index) {
  const route = screen.metadata?.route || screen.route || `screen-${index + 1}`;
  return `${String(index + 1).padStart(3, '0')}-${slugify(route.replace(/^\//, ''), 'screen')}`;
}

function dataUrlToZipPayload(dataUrl) {
  const value = String(dataUrl || '');
  const match = value.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return { data: value, options: {} };
  return { data: match[2], options: { base64: true } };
}

function selectedElementLine(screen) {
  if (screen.metadata?.captureMode !== 'selected-element') return '';
  const element = screen.metadata?.element;
  if (!element) return '';
  const label = element.accessibleName || element.textSnippet || '';
  const descriptor = [element.tagName || 'element', label ? `"${label}"` : null]
    .filter(Boolean)
    .join(' ');
  const hint = element.selectorHint ? ` — \`${element.selectorHint}\`` : '';
  return `- Selected element: ${descriptor}${hint}\n`;
}

function annotationLines(screen) {
  const annotations = screen.metadata?.annotations || [];
  if (annotations.length === 0) return '- No annotations recorded.';
  return annotations
    .map(
      (annotation) =>
        `- ${annotation.id} (${annotation.type}): ${annotationText(annotation) || 'No note entered.'}`,
    )
    .join('\n');
}

export function buildFeedbackZipFileName(pkg, date = new Date()) {
  return `${timestampForFile(date)}-prime-feedback-${slugify(pkg?.title, pkg?.id || 'package')}.zip`;
}

export function buildFeedbackManifest(pkg, options = {}) {
  const exportedAt = options.exportedAt || new Date().toISOString();
  return {
    schemaVersion: '1.1',
    packageId: pkg.id,
    appName: 'DHA PRIME',
    feature: 'Feedback Markup Session',
    status: options.finalStatus || pkg.status || 'draft',
    createdAt: pkg.createdAt,
    exportedAt,
    title: pkg.title,
    overallInstructions: pkg.overallInstructions || '',
    user: {
      id: pkg.createdBy?.id || 'unknown',
      displayName: pkg.createdBy?.displayName || 'Unknown user',
    },
    persona: {
      name: pkg.createdBy?.persona || 'Shell user',
      roles: pkg.createdBy?.roles || [],
    },
    browser: options.browser || {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      language: typeof navigator !== 'undefined' ? navigator.language : 'en-US',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    screens: (pkg.screens || []).map((screen, index) => {
      const base = screenBaseName(screen, index);
      return {
        screenId:
          screen.screenId || screen.metadata?.screenId || `S-${String(index + 1).padStart(3, '0')}`,
        route: screen.metadata?.route || '',
        screenshot: `screens/${base}.png`,
        annotatedSvg: `screens/${base}-annotated.svg`,
        metadata: `screens/${base}.json`,
        annotationCount: screen.metadata?.annotations?.length || 0,
      };
    }),
  };
}

export async function buildFeedbackZipBlob(pkg, options = {}) {
  const zip = new JSZip();
  const manifest = buildFeedbackManifest(pkg, options);
  const screens = pkg.screens || [];

  zip.file(
    'README.md',
    `# ${pkg.title || 'DHA PRIME Feedback Package'}

This package contains annotated DHA PRIME feedback evidence for developer handoff.

- Package ID: ${pkg.id}
- Status: ${manifest.status}
- Screens: ${screens.length}
- Exported: ${manifest.exportedAt}
`,
  );

  zip.file(
    'feedback-summary.md',
    `# Feedback Summary

## Overall Instructions

${pkg.overallInstructions || 'No overall instructions entered.'}

${screens
  .map(
    (
      screen,
      index,
    ) => `## ${index + 1}. ${screen.metadata?.pageTitle || screen.metadata?.route || 'Screen'}

- Route: \`${screen.metadata?.route || '/'}\`
- Captured: ${screen.metadata?.capturedAt || screen.savedAt || 'unknown'}
- Capture mode: ${screen.metadata?.captureMode || 'visible-viewport'}
${selectedElementLine(screen)}
${annotationLines(screen)}
`,
  )
  .join('\n')}
`,
  );
  zip.file('developer-checklist.md', DEVELOPER_CHECKLIST);
  zip.file('feedback.json', JSON.stringify(manifest, null, 2));

  screens.forEach((screen, index) => {
    const base = screenBaseName(screen, index);
    const png = dataUrlToZipPayload(screen.screenshotDataUrl);
    zip.file(`screens/${base}.png`, png.data, png.options);
    zip.file(`screens/${base}-annotated.svg`, screen.annotatedSvg || '<svg></svg>');
    zip.file(`screens/${base}.json`, JSON.stringify(screen.metadata || {}, null, 2));
  });

  return zip.generateAsync({ type: 'blob' });
}

export async function downloadFeedbackZip(pkg, options = {}) {
  const prepared = await prepareFeedbackZipDownload(pkg, options);
  const url = URL.createObjectURL(prepared.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = prepared.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename: prepared.filename, exportedAt: prepared.exportedAt };
}

export async function prepareFeedbackZipDownload(pkg, options = {}) {
  const exportedAt = options.exportedAt || new Date().toISOString();
  const blob = await buildFeedbackZipBlob(pkg, { ...options, exportedAt });
  const filename = buildFeedbackZipFileName(pkg, new Date(exportedAt));
  return { blob, filename, exportedAt };
}
